/*
 * MarketTicker.jsx  -  Ethikally scrolling index strip (Cloudflare version)
 * Location: frontend/src/components/MarketTicker.jsx
 *
 * Reads data from /api/ticker (frontend/functions/api/ticker.js on Cloudflare).
 * Each visitor's browser also keeps a copy for 30 minutes.
 * If data can't be loaded, the strip hides itself instead of showing an error.
 */
import { useEffect, useState } from "react";

const CACHE_KEY = "eth-ticker-v2";
const CACHE_MINUTES = 30;

const fmt = (n) =>
  Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function readCache(allowStale) {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { time, indices } = JSON.parse(raw);
    const fresh = Date.now() - time < CACHE_MINUTES * 60 * 1000;
    return fresh || allowStale ? indices : null;
  } catch {
    return null;
  }
}

function writeCache(indices) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ time: Date.now(), indices }));
  } catch {
    /* storage unavailable; carry on without caching */
  }
}

export default function MarketTicker() {
  const [indices, setIndices] = useState(() => readCache(false) || []);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (readCache(false)) return; // still fresh, nothing to do
      try {
        const res = await fetch("/api/ticker");
        if (!res.ok) throw new Error("bad response");
        const json = await res.json();
        if (!active) return;
        if (Array.isArray(json.indices) && json.indices.length) {
          setIndices(json.indices);
          writeCache(json.indices);
          return;
        }
        throw new Error("no data");
      } catch {
        const stale = readCache(true); // show last known data if any
        if (active && stale) setIndices(stale);
      }
    };

    load();
    const timer = setInterval(load, CACHE_MINUTES * 60 * 1000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  if (indices.length === 0) return null;

  // Two copies back to back so the scroll loops without a gap
  const loop = [...indices, ...indices];

  return (
    <div className="eth-ticker" role="region" aria-label="Market indices, delayed">
      <style>{`
        .eth-ticker {
          position: relative;
          overflow: hidden;
          background: var(--ticker-bg, #14263d);
          color: var(--ticker-text, #e8edf3);
          font-size: 13px;
          line-height: 1;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .eth-ticker-track {
          display: flex;
          width: max-content;
          animation: eth-scroll 60s linear infinite;
        }
        .eth-ticker:hover .eth-ticker-track { animation-play-state: paused; }
        .eth-ticker-item {
          display: flex;
          gap: 8px;
          align-items: baseline;
          padding: 10px 22px;
          white-space: nowrap;
          border-right: 1px solid rgba(255,255,255,0.08);
        }
        .eth-ticker-name { font-weight: 600; }
        .eth-ticker-price { font-variant-numeric: tabular-nums; }
        .eth-up   { color: var(--ticker-up, #4fbf85); font-variant-numeric: tabular-nums; }
        .eth-down { color: var(--ticker-down, #ea6a6a); font-variant-numeric: tabular-nums; }
        .eth-ticker-note {
          position: absolute;
          right: 0; top: 0; bottom: 0;
          display: flex; align-items: center;
          padding: 0 12px 0 28px;
          font-size: 11px;
          opacity: 0.85;
          background: linear-gradient(to right, transparent, var(--ticker-bg, #14263d) 30%);
        }
        @keyframes eth-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .eth-ticker { overflow-x: auto; }
          .eth-ticker-track { animation: none; }
        }
        @media (max-width: 600px) {
          .eth-ticker-note { display: none; }
        }
      `}</style>

      <div className="eth-ticker-track">
        {loop.map((ix, i) => {
          const up = ix.change >= 0;
          return (
            <div className="eth-ticker-item" key={i} aria-hidden={i >= indices.length}>
              <span className="eth-ticker-name">{ix.name}</span>
              <span className="eth-ticker-price">{fmt(ix.price)}</span>
              <span className={up ? "eth-up" : "eth-down"}>
                {up ? "▲" : "▼"} {fmt(Math.abs(ix.change))} ({up ? "+" : "-"}
                {Math.abs(ix.changePct).toFixed(2)}%)
              </span>
            </div>
          );
        })}
      </div>

      <div className="eth-ticker-note">Delayed data, for information only</div>
    </div>
  );
}
